/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import parse from "html-react-parser";
import React from "react";
import { useSelector } from "react-redux";

import { format } from "@gamedev-web/gamedev-sdk-core";

import { getMostAdvancedGameConcept } from "../../helpers/gameConceptHelpers";
import { compareIdXboxApplicationStepStatus } from "../../helpers/idXboxStatusHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus, CurrentGameConcept } from "../../models/crmStatus";
import { IdXboxApplicationStep, NdaStatus } from "../../models/status";
import {
  getCurrentGameConcept,
  getGameConceptsList,
  getIdXboxApplicationStep,
  getIsGdkReceived,
  getNdaStatus,
} from "../../redux/selectors";
import { getOnboardingUser } from "../../redux/selectors/authSelectors";
import { IdXboxGameConceptDataSet } from "../../server/crm/api/models/forms";
import { ContentBoxIcon } from "../ContentBox";
import { addBoldTags, InfoBoxBase } from "../InfoBoxBase";

export const InfoBoxAdditionalAgreements = () => {
  const [
    infoBoxAdditionalAgreementsTitle,
    infoBoxCompleteGameInformation,
    infoBoxSubmitGameInformation,
    infoBoxSubmitGameInformationRequest,
    infoBoxAdditionalAgreementsUnderReview,
    infoBoxAdditionalAgreementsUnderReviewMessage,
    infoBoxAdditionalAgreementsSignActionLink,
    infoBoxAdditionalAgreementsSignTlaActionRequested,
    infoBoxAdditionalAgreementsSignGdkActionRequested,
    infoBoxAdditionalAgreementsSignTlaAndGdkActionRequested,
    infoBoxAdditionalAgreementsRegisteredEmail,
  ] = useStrings(
    "infoBoxAdditionalAgreementsTitle",
    "infoBoxCompleteGameInformation",
    "infoBoxSubmitGameInformation",
    "infoBoxSubmitGameInformationRequest",
    "infoBoxAdditionalAgreementsUnderReview",
    "infoBoxAdditionalAgreementsUnderReviewMessage",
    "infoBoxAdditionalAgreementsSignActionLink",
    "infoBoxAdditionalAgreementsSignTlaActionRequested",
    "infoBoxAdditionalAgreementsSignGdkActionRequested",
    "infoBoxAdditionalAgreementsSignTlaAndGdkActionRequested",
    "infoBoxAdditionalAgreementsRegisteredEmail"
  );

  const userInfo = useSelector(getOnboardingUser);
  const step = useSelector(getIdXboxApplicationStep);
  const isGdkReceived = useSelector(getIsGdkReceived);
  const currentGameConcept: CurrentGameConcept | undefined = useSelector(getCurrentGameConcept) as
    | CurrentGameConcept
    | undefined;

  if (
    compareIdXboxApplicationStepStatus(step, IdXboxApplicationStep.ApplicationReadyForReview) < 0 ||
    currentGameConcept?.crmGameConceptStatus === CrmGameConceptStatus.MoreInfoNeeded
  ) {
    /* before complete the Game concept info */
    return (
      <InfoBoxBase
        title={infoBoxAdditionalAgreementsTitle}
        icon={ContentBoxIcon.Lock}
        rightSideText={infoBoxCompleteGameInformation}
      />
    );
  }

  if (
    compareIdXboxApplicationStepStatus(step, IdXboxApplicationStep.ApplicationReadyForReview) === 0
  ) {
    /* game concept info complete but submit pending */
    const mainText = parse(
      format(
        infoBoxSubmitGameInformationRequest,
        userInfo && userInfo.emailAddress
          ? addBoldTags(userInfo.emailAddress)
          : addBoldTags(infoBoxAdditionalAgreementsRegisteredEmail)
      )
    );
    return (
      <InfoBoxBase
        title={infoBoxAdditionalAgreementsTitle}
        icon={ContentBoxIcon.Lock}
        rightSideText={infoBoxSubmitGameInformation}
        mainText={mainText}
      />
    );
  }

  if (
    compareIdXboxApplicationStepStatus(step, IdXboxApplicationStep.ApplicationIsBeingReviewed) === 0
  ) {
    /* game concept submit, additional agreements not sent */
    const mainText = parse(
      format(
        infoBoxAdditionalAgreementsUnderReviewMessage,
        userInfo && userInfo.emailAddress
          ? addBoldTags(userInfo.emailAddress)
          : addBoldTags(infoBoxAdditionalAgreementsRegisteredEmail)
      )
    );
    return (
      <InfoBoxBase
        title={infoBoxAdditionalAgreementsTitle}
        icon={ContentBoxIcon.Timer}
        rightSideText={infoBoxAdditionalAgreementsUnderReview}
        mainText={mainText}
      />
    );
  }

  if (compareIdXboxApplicationStepStatus(step, IdXboxApplicationStep.AdditionalAgreements) === 0) {
    /* TLA and/or GDK contracts waiting to be signed */
    // ApplicationStep.AdditionalAgreements covers for the Game Concept's TLA contract and the onboarding-level GDK contract.
    // Based on which contracts are signed, we display different messages in this box.
    // TODO: Add docusign link
    const docusignLink = "";
    let mainText: string | JSX.Element | JSX.Element[] = [];
    const isTlaSigned = currentGameConcept?.crmGameConceptStatus === CrmGameConceptStatus.Accepted;
    if (!isTlaSigned && !isGdkReceived) {
      mainText = parse(addBoldTags(infoBoxAdditionalAgreementsSignTlaAndGdkActionRequested));
    } else {
      mainText = parse(
        addBoldTags(
          !isTlaSigned
            ? infoBoxAdditionalAgreementsSignTlaActionRequested
            : infoBoxAdditionalAgreementsSignGdkActionRequested
        )
      );
    }

    return (
      <InfoBoxBase
        title={infoBoxAdditionalAgreementsTitle}
        icon={ContentBoxIcon.GreenCircle}
        mainText={mainText}
        actionRequiredText={infoBoxAdditionalAgreementsSignActionLink}
        actionLink={docusignLink}
      />
    );
  }

  /* TLA signed (ApplicationStep.ApplicationComplete) */
  /* Any other scenario */
  return null;
};

export const IdXboxInfoAdditionalAgreements = () => {
  const [
    infoBoxAdditionalAgreementsTitle,
    infoBoxWelcomePageSubmitGameInformation,
    infoBoxSubmitGameInformationRequest,
    infoBoxAdditionalAgreementsUnderReview,
    infoBoxAdditionalAgreementsUnderReviewMessage,
    infoBoxAdditionalAgreementsSignActionLink,
    infoBoxAdditionalAgreementsSignTlaActionRequested,
    infoBoxAdditionalAgreementsSignGdkActionRequested,
    infoBoxAdditionalAgreementsSignTlaAndGdkActionRequested,
    infoBoxAdditionalAgreementsRegisteredEmail,
  ] = useStrings(
    "infoBoxAdditionalAgreementsTitle",
    "infoBoxWelcomePageSubmitGameInformation",
    "infoBoxSubmitGameInformationRequest",
    "infoBoxAdditionalAgreementsUnderReview",
    "infoBoxAdditionalAgreementsUnderReviewMessage",
    "infoBoxAdditionalAgreementsSignActionLink",
    "infoBoxAdditionalAgreementsSignTlaActionRequested",
    "infoBoxAdditionalAgreementsSignGdkActionRequested",
    "infoBoxAdditionalAgreementsSignTlaAndGdkActionRequested",
    "infoBoxAdditionalAgreementsRegisteredEmail"
  );

  const userInfo = useSelector(getOnboardingUser);
  const gameConceptsList = useSelector(getGameConceptsList) as IdXboxGameConceptDataSet;
  const isGdkReceived = useSelector(getIsGdkReceived);
  const ndaStatus = useSelector(getNdaStatus);

  const mostAdvancedGameConcept = getMostAdvancedGameConcept(gameConceptsList);
  const mostAdvancedStatus = mostAdvancedGameConcept?.state.status;
  if (mostAdvancedStatus === CrmGameConceptStatus.UnderReview) {
    // Game concept is under review
    return (
      <InfoBoxBase
        title={infoBoxAdditionalAgreementsTitle}
        icon={ContentBoxIcon.Timer}
        rightSideText={infoBoxAdditionalAgreementsUnderReview}
        mainText={parse(
          format(
            infoBoxAdditionalAgreementsUnderReviewMessage,
            userInfo && userInfo.emailAddress
              ? addBoldTags(userInfo.emailAddress)
              : addBoldTags(infoBoxAdditionalAgreementsRegisteredEmail)
          )
        )}
      />
    );
  }

  const isTlaSent = mostAdvancedStatus === CrmGameConceptStatus.AdditionalContractsSent;
  const isTlaSigned = mostAdvancedStatus === CrmGameConceptStatus.Accepted;
  if (isTlaSent || (isTlaSigned && !isGdkReceived)) {
    // TLA was sent OR TLA was signed but GDK is still pending signing
    return (
      <InfoBoxBase
        title={infoBoxAdditionalAgreementsTitle}
        icon={ContentBoxIcon.GreenCircle}
        mainText={parse(
          addBoldTags(
            !isTlaSigned && !isGdkReceived
              ? infoBoxAdditionalAgreementsSignTlaAndGdkActionRequested
              : !isTlaSigned
              ? infoBoxAdditionalAgreementsSignTlaActionRequested
              : infoBoxAdditionalAgreementsSignGdkActionRequested
          )
        )}
        actionRequiredText={infoBoxAdditionalAgreementsSignActionLink}
      />
    );
  }

  if (isTlaSigned && isGdkReceived) {
    // Both TLA and GDK are signed
    return null;
  }

  if (ndaStatus === NdaStatus.Signed) {
    return (
      <InfoBoxBase
        title={infoBoxAdditionalAgreementsTitle}
        icon={ContentBoxIcon.Lock}
        rightSideText={infoBoxWelcomePageSubmitGameInformation}
        mainText={parse(
          format(
            infoBoxSubmitGameInformationRequest,
            userInfo && userInfo.emailAddress
              ? addBoldTags(userInfo.emailAddress)
              : addBoldTags(infoBoxAdditionalAgreementsRegisteredEmail)
          )
        )}
      />
    );
  }

  // User still working on entering game concept information
  return (
    <InfoBoxBase
      title={infoBoxAdditionalAgreementsTitle}
      icon={ContentBoxIcon.Lock}
      rightSideText={infoBoxWelcomePageSubmitGameInformation}
    />
  );
};
